
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Profile } from './types';
import { fetchUserProfile } from './fetchUserProfile';

/**
 * Login with email and password
 */
export const login = async (email: string, password: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Login error:', error.message);
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive"
      });
      return false;
    }
    
    toast({
      title: "Welcome back!",
      description: `Logged in successfully`,
    });
    
    return true;
  } catch (error) {
    console.error('Exception during login:', error);
    toast({
      title: "Login failed",
      description: "An unexpected error occurred",
      variant: "destructive"
    });
    return false;
  }
};

/**
 * Sign up with email and password
 */
export const signup = async (email: string, password: string, name: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: {
          name: name,
        },
      }
    });

    if (error) {
      console.error('Signup error:', error.message);
      toast({
        title: "Signup failed",
        description: error.message,
        variant: "destructive"
      });
      return false;
    }

    toast({
      title: "Account created",
      description: "Your account has been created successfully",
    });
    return true;
  } catch (error) {
    console.error('Exception during signup:', error);
    toast({
      title: "Signup failed",
      description: "An unexpected error occurred",
      variant: "destructive"
    });
    return false;
  }
};

/**
 * Logout current user
 */
export const logout = async (): Promise<void> => {
  try {
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      console.error('Logout error:', error.message);
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive"
      });
      return;
    }
    
    toast({
      title: "Logged out",
      description: "You've been successfully logged out"
    });
    
  } catch (error) {
    console.error('Exception during logout:', error);
    toast({
      title: "Logout failed",
      description: "An unexpected error occurred",
      variant: "destructive"
    });
  }
};

/**
 * Update user password
 */
export const updatePassword = async (
  currentPassword: string, 
  newPassword: string
): Promise<{success: boolean, message: string}> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !user.email) {
      return {
        success: false,
        message: 'User not found'
      };
    }
    
    // Use Supabase's built-in password update which handles verification internally
    const { error: updateError } = await supabase.auth.updateUser({ 
      password: newPassword 
    });
    
    if (updateError) {
      console.error('Password update failed:', updateError.message);
      return {
        success: false,
        message: updateError.message || 'Failed to update password'
      };
    }
    
    return {
      success: true,
      message: 'Password updated successfully'
    };
  } catch (error) {
    console.error('Exception updating password:', error);
    return {
      success: false,
      message: 'An unexpected error occurred'
    };
  }
};

/**
 * Update user profile
 */
export const updateProfile = async (
  userId: string,
  data: Partial<Profile>
): Promise<boolean> => {
  if (!userId) return false;
  
  try {
    // Extract basic profile fields for profiles table
    const profileData = {
      name: data.name,
      // Add other fields as needed
    };
    
    // Only update fields that are provided
    const filteredProfileData = Object.fromEntries(
      Object.entries(profileData).filter(([_, v]) => v !== undefined)
    ) as { name?: string };
    
    if (Object.keys(filteredProfileData).length > 0) {
      const { error } = await supabase
        .from('profiles')
        .update(filteredProfileData)
        .eq('id', userId);
        
      if (error) {
        console.error('Error updating profile:', error);
        toast({
          title: "Update failed",
          description: error.message,
          variant: "destructive"
        });
        return false;
      }
    }
    
    toast({
      title: "Profile updated",
      description: "Your profile has been updated successfully"
    });
    return true;
  } catch (error) {
    console.error('Exception updating profile:', error);
    toast({
      title: "Update failed",
      description: "An unexpected error occurred",
      variant: "destructive"
    });
    return false;
  }
};
